import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import en from '@/locales/en.json'
import {
  AppLocale,
  isRtlLocale,
  isSupportedLocale,
  LOCALE_BY_CODE,
  SUPPORTED_LOCALES,
} from './localeConfig'
import { localeLoaders } from './localeLoaders'

export const LOCALE_STORAGE_KEY = 'locale'

export { SUPPORTED_LOCALES, type AppLocale, LOCALE_BY_CODE, isRtlLocale, isSupportedLocale }
export { LOCALE_DEFINITIONS } from './localeConfig'

const loadedLocales = new Set<AppLocale>(['en'])

export async function ensureLocaleLoaded(locale: AppLocale): Promise<void> {
  if (loadedLocales.has(locale)) return
  const mod = await localeLoaders[locale]()
  i18n.addResourceBundle(locale, 'translation', mod.default, true, true)
  loadedLocales.add(locale)
}

function applyDocumentLocale(locale: AppLocale) {
  if (typeof document === 'undefined') return
  const def = LOCALE_BY_CODE[locale]
  document.documentElement.lang = def.languageTag
  document.documentElement.dir = isRtlLocale(locale) ? 'rtl' : 'ltr'
}

function readStoredLocale(): AppLocale {
  if (typeof window === 'undefined') return 'en'
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
  if (isSupportedLocale(stored)) return stored
  // Migrate legacy full tags (e.g. zh-CN → zh)
  const base = stored?.split('-')[0]
  if (isSupportedLocale(base)) return base
  return 'en'
}

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources: { en: { translation: en } },
    lng: typeof window !== 'undefined' ? readStoredLocale() : 'en',
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LOCALES],
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  })
}

export function syncLocaleFromStorage() {
  const locale = readStoredLocale()
  void (async () => {
    await ensureLocaleLoaded(locale)
    if (i18n.language !== locale) {
      await i18n.changeLanguage(locale)
    }
    applyDocumentLocale(locale)
  })()
}

export function setAppLocale(locale: AppLocale) {
  localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  applyDocumentLocale(locale)
  return (async () => {
    await ensureLocaleLoaded(locale)
    return i18n.changeLanguage(locale)
  })()
}

export default i18n
